/** /t/signup — a teacher opens their own account on a server that allows it
 * (services/auth signup.py, `AUTH_TEACHER_SIGNUP=open`).
 *
 * One primary action: create the account. That does not sign in: the account is usable only
 * once the address is confirmed (owner decision 2026-10-12, enforced by the issuer), so the
 * form gives way to "check your email" with the address, a resend, and what to do when no
 * message comes. Where the server does not take sign-ups, the screen says how an account is
 * opened there (an institution admin's invite) instead of showing a form it would refuse.
 * Students never reach this: they join with a code and need no account, which the screen says.
 */
import { Link } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ResendConfirmation } from "../auth/ResendConfirmation";
import { SignupRefused, signUpTeacher } from "../auth/session";
import { MIN_PASSWORD, signupErrorKey, signupProblem, type SignupProblem } from "../auth/signupRules";
import { useSignupPolicy } from "../auth/useSignupPolicy";
import { product } from "../product";
import { Button, PasswordInput } from "../ui";

const link = "inline-flex min-h-[var(--control-h)] items-center font-medium underline underline-offset-4 hover:decoration-2";

export function TeacherSignUp() {
  const { t } = useTranslation();
  const { policy, loading } = useSignupPolicy();

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 px-6 py-10">
      <Link to="/" className="wordmark self-start text-2xl" translate="no">
        {product.name}
      </Link>
      {loading && <p role="status" className="text-[color:var(--ink-2)]">{t("teacherSignup.checking")}</p>}
      {!loading && policy === "open" && <SignupForm />}
      {!loading && policy !== "open" && <Closed />}
      <p className="text-sm text-[color:var(--ink-2)]">{t("teacherSignup.students")}</p>
    </main>
  );
}

function Closed() {
  const { t } = useTranslation();
  return (
    <section className="flex flex-col gap-3">
      <h1 className="display text-2xl">{t("teacherSignup.closedTitle")}</h1>
      <p className="text-[color:var(--ink-2)]">{t("teacherSignup.closedBody")}</p>
      <Link to="/signin" className={link}>
        {t("teacherSignup.toSignIn")}
      </Link>
    </section>
  );
}

/** After sign-up: the account waits for the link in the mail. */
function CheckEmail({ email }: { email: string }) {
  const { t } = useTranslation();
  const heading = useRef<HTMLHeadingElement>(null);
  // the form this replaced held focus; move it to the news so a screen reader hears it
  useEffect(() => heading.current?.focus(), []);
  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-2">
        <h1 ref={heading} tabIndex={-1} className="display text-2xl sm:text-3xl">
          {t("teacherSignup.checkEmail.title")}
        </h1>
        <p className="text-[color:var(--ink-2)]">
          {t("teacherSignup.checkEmail.sent")}{" "}
          <strong className="break-all font-semibold text-[color:var(--ink)]" translate="no">
            {email}
          </strong>
        </p>
        <p className="text-[color:var(--ink-2)]">{t("teacherSignup.checkEmail.next")}</p>
      </header>
      <section className="flex flex-col gap-3 rounded-[var(--radius-card)] border border-[color:var(--line)] bg-[color:var(--surface)] p-4">
        <h2 className="font-semibold">{t("teacherSignup.checkEmail.noMailTitle")}</h2>
        <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-[color:var(--ink-2)]">
          <li>{t("teacherSignup.checkEmail.noMail.wait")}</li>
          <li>{t("teacherSignup.checkEmail.noMail.resend")}</li>
          <li>{t("teacherSignup.checkEmail.noMail.wrong")}</li>
        </ul>
        <ResendConfirmation email={email} />
      </section>
      <Link to="/signin" className={link}>
        {t("teacherSignup.toSignIn")}
      </Link>
    </section>
  );
}

function SignupForm() {
  const { t } = useTranslation();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<SignupProblem>(null);
  const [error, setError] = useState<string | null>(null);
  const emailHint = useId();

  async function submit() {
    const p = signupProblem({ email, password });
    setProblem(p);
    setError(null);
    if (p) return;
    setBusy(true);
    try {
      await signUpTeacher({ email: email.trim(), password, display_name: name.trim() || undefined });
      setPassword("");
      setSentTo(email.trim().toLowerCase());
    } catch (e) {
      const status = e instanceof SignupRefused ? e.status : -1;
      setError(t(`teacherSignup.errors.${signupErrorKey(status)}`));
      setBusy(false);
    }
  }

  if (sentTo) return <CheckEmail email={sentTo} />;

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <h1 className="display text-2xl sm:text-3xl">{t("teacherSignup.title")}</h1>
        <p className="text-[color:var(--ink-2)]">{t("teacherSignup.lede")}</p>
      </header>
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t("teacherSignup.name")}</span>
          <input className="field" name="name" autoComplete="name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-sm font-medium">{t("teacherSignup.email")}</span>
          <input
            className="field"
            type="email"
            name="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            required
            maxLength={200}
            aria-invalid={problem === "email" || undefined}
            aria-describedby={problem === "email" ? emailHint : undefined}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {problem === "email" && (
            <span id={emailHint} role="alert" className="text-sm text-[color:var(--danger)]">
              {t("teacherSignup.problems.email")}
            </span>
          )}
        </label>
        <PasswordInput
          label={t("teacherSignup.password")}
          hint={problem === "password" ? t("teacherSignup.problems.password", { n: MIN_PASSWORD }) : t("teacherSignup.passwordHint", { n: MIN_PASSWORD })}
          invalid={problem === "password"}
          name="password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD}
          required
          value={password}
          onChange={setPassword}
        />
        {error && (
          <p role="alert" className="text-sm text-[color:var(--danger)]">
            {error}
          </p>
        )}
        <Button type="submit" block loading={busy}>
          {t("teacherSignup.cta")}
        </Button>
      </form>
      <p className="text-sm">
        {t("teacherSignup.haveAccount")}{" "}
        <Link to="/signin" className={link}>
          {t("signin.cta")}
        </Link>
      </p>
    </section>
  );
}
